import './TalkToFamily.scss';

import { type MouseEvent, type ReactNode, useState } from 'react';
import { Container } from 'react-bootstrap';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { faCommentSms, faEnvelope, faFilePdf, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { MarketingSectionIntro } from '@components/marketing/MarketingSectionIntro/MarketingSectionIntro';

interface TalkHandout {
  title: string;
  meta: string;
}

const TALK_TIPS = [
  {
    lead: 'Lead with the relationship, not the result.',
    body: '"I want to share something from a recent health test of mine."',
  },
  {
    lead: 'Name what was found, simply.',
    body: "There's a gene change (variant) that can be inherited, meaning relatives in our family carry it too.",
  },
  {
    lead: 'Make the choice theirs.',
    body: '"MyOme will email you a link. Looking is up to you. You can also decline. That\'s totally fine."',
  },
  {
    lead: 'Leave a door open.',
    body: '"I\'ll be around for any questions. No pressure on timing."',
  },
];

const TALK_HANDOUTS: TalkHandout[] = [];

type TalkChannel = 'text' | 'email' | 'phone';

interface TalkChannelContent {
  id: TalkChannel;
  tabLabel: string;
  meta: string;
  message: ReactNode;
  copyText?: string;
  note?: string;
}

const TALK_CHANNELS: TalkChannelContent[] = [
  {
    id: 'text',
    tabLabel: 'Text message',
    meta: 'Suggested text',
    copyText:
      'Hey! I had genetic testing done for hypoparathyroidism and learned that I have a change in the calcium sensing receptor gene, CASR. Changes in CASR are linked to autosomal dominant hypocalcemia type 1, or ADH1, a genetic condition causing low blood calcium that can run in families.',
    message: (
      <>
        {
          'Hey! I had genetic testing done for hypoparathyroidism and learned that I have a change in the calcium sensing receptor gene, '
        }
        <i>CASR</i>
        {'. Changes in '}
        <i>CASR</i>
        {
          ' are linked to autosomal dominant hypocalcemia type 1, or ADH1, a genetic condition causing low blood calcium that can run in families.'
        }
      </>
    ),
    note: 'Edit it to sound like you. You can share the details in person.',
  },
  {
    id: 'email',
    tabLabel: 'Email',
    meta: 'Suggested email',
    message: `Subject: Something from my recent health testing when you have a minute

Hi [Name],

I had some genetic testing done through my doctor recently, and one of the findings is something that could affect relatives in our family, including you. It's not urgent, but the lab (MyOme) is offering no-charge genetic testing and an appointment with a genetic counselor to eligible blood relatives so you can find out for yourself. This program is sponsored by a company called BridgeBio.

In the next day or two you'll get a separate email from MyOme with a link. I wanted you to hear from me first so it doesn't come out of the blue.

Love,
[Your name]`,
    note: 'Replace [Name] and [Your name]. Consider attaching one of the handouts below.',
  },
  {
    id: 'phone',
    tabLabel: 'Talking points',
    meta: 'Talking points',
    message: `• Lead with the relationship, not the result. "I want to share something from a recent health test of mine."
• Name what was found, simply. "There's a gene change (variant) that can be inherited, meaning relatives in our family carry it too."
• Make the choice theirs. "MyOme will email you a link. Looking is up to you. You can also decline. That's totally fine."
• Offer a community resource. "There's a patient association, the HypoPARAthyroidism Association (HPA), if you want to talk to people who've been through this."
• Leave a door open. "I'll be around for any questions. No pressure on timing."`,
  },
];

const TAB_ICONS: Record<TalkChannel, IconDefinition> = {
  text: faCommentSms,
  email: faEnvelope,
  phone: faPhone,
};

const onPlaceholderLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
};

export const TalkToFamily = () => {
  const [activeChannel, setActiveChannel] = useState<TalkChannel>('text');
  const [copied, setCopied] = useState(false);

  const activeContent = TALK_CHANNELS.find(ch => ch.id === activeChannel) ?? TALK_CHANNELS[0];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        activeContent.copyText ?? (typeof activeContent.message === 'string' ? activeContent.message : ''),
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard may be unavailable in some browsers.
    }
  };

  return (
    <section className='fvt-talk fvt-section--tinted py-6' aria-labelledby='fvt-talk-heading'>
      <Container fluid='xxl'>
        <div className='fvt-talk__grid gap-4 gap-lg-5 align-items-start mt-4'>
          <div className='fvt-talk__left'>
            <MarketingSectionIntro
              variant='fvt'
              eyebrow='Before you invite them'
              heading='A quick heads-up goes a long way'
              headingId='fvt-talk-heading'
              headingClassName='fvt-talk__heading'
            >
              <p className='mb-3'>
                Most relatives are more likely to respond if they hear from you first. Otherwise, an unexpected email
                from MyOme may catch them off guard - especially since your name won’t appear in the subject line.
              </p>
              <p>
                We recommend that you start with a quick text or call. Consider using one of the handouts to guide the
                conversation, then come back here to send the official invite.
              </p>
            </MarketingSectionIntro>

            <div className='fvt-talk__tips pt-4 mt-4'>
              <h3 className='fvt-talk__tips-heading mb-3'>How to open the conversation</h3>
              <ol className='fvt-talk__tips-list gap-3 p-0 m-0'>
                {TALK_TIPS.map(tip => (
                  <li key={tip.lead}>
                    <strong className='d-block mb-0'>{tip.lead}</strong>
                    {tip.body}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className='fvt-talk__right'>
            <div className='fvt-talk-tabs'>
              <div className='fvt-talk-tabs__nav d-flex' role='tablist' aria-label='Message format'>
                {TALK_CHANNELS.map(channel => (
                  <button
                    key={channel.id}
                    type='button'
                    role='tab'
                    id={`fvt-talk-tab-${channel.id}`}
                    aria-controls={`fvt-talk-panel-${channel.id}`}
                    aria-selected={activeChannel === channel.id}
                    tabIndex={activeChannel === channel.id ? 0 : -1}
                    className={`fvt-talk-tabs__tab d-inline-flex flex-fill gap-2 align-items-center justify-content-center py-3 px-4${activeChannel === channel.id ? ' fvt-talk-tabs__tab--active' : ''}`}
                    onClick={() => setActiveChannel(channel.id)}
                  >
                    <FontAwesomeIcon icon={TAB_ICONS[channel.id]} aria-hidden='true' />
                    {channel.tabLabel}
                  </button>
                ))}
              </div>

              <div className='fvt-talk-tabs__body px-4 pt-4 pb-3'>
                {TALK_CHANNELS.map(channel => (
                  <div
                    key={channel.id}
                    id={`fvt-talk-panel-${channel.id}`}
                    role='tabpanel'
                    aria-labelledby={`fvt-talk-tab-${channel.id}`}
                    hidden={activeChannel !== channel.id}
                    className='fvt-talk-tabs__panel'
                  >
                    <div className='fvt-talk-tabs__meta d-flex gap-3 align-items-center mb-3'>
                      <span>{channel.meta}</span>
                      {activeChannel === channel.id && (
                        <button
                          type='button'
                          className={`fvt-talk-tabs__copy d-inline-flex gap-1 align-items-center py-1 px-3 ms-auto${copied ? ' fvt-talk-tabs__copy--copied' : ''}`}
                          onClick={handleCopy}
                        >
                          <FontAwesomeIcon icon={faCopy} aria-hidden='true' />
                          {copied ? 'Copied' : 'Copy'}
                        </button>
                      )}
                    </div>
                    <div className='fvt-talk-tabs__message p-3'>{channel.message}</div>
                    {channel.note && <p className='fvt-talk-tabs__note mt-2 mb-0'>{channel.note}</p>}
                  </div>
                ))}
              </div>
            </div>

            {TALK_HANDOUTS.length > 0 && (
              <div className='fvt-talk__handouts gap-3 mt-4'>
                {TALK_HANDOUTS.map(handout => (
                  <a
                    key={handout.title}
                    href='#'
                    className='fvt-talk-handout d-flex gap-3 align-items-center p-3'
                    aria-disabled='true'
                    onClick={onPlaceholderLinkClick}
                  >
                    <span className='fvt-talk-handout__icon flex-shrink-0' aria-hidden='true'>
                      <FontAwesomeIcon icon={faFilePdf} />
                    </span>
                    <span className='fvt-talk-handout__info d-flex flex-column flex-fill gap-1'>
                      <span className='fvt-talk-handout__title'>{handout.title}</span>
                      <span className='fvt-talk-handout__meta'>{handout.meta}</span>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
};
